/**
 * Light theme palette — the counterpart to colors.ts (dark).
 *
 * MUST mirror the exact shape of `colors` in ./colors.ts, key for key: the CSS
 * variable generator pairs the two by matching keys, so a missing or extra key
 * would silently drop a token. Values follow the palette study:
 *   - neutrals invert (dark grounds -> warm off-whites; light text -> warm ink);
 *   - the brand scarlet is preserved;
 *   - greens / oranges / yellows / purples deepen so they hold on white;
 *   - elevation moves from "lighter surface" to "shadow" (handled in effects),
 *     so surface values compress toward white here.
 */
import { colorTokens } from './tokens';

// Keys whose value IS a shared semantic token read it from ./tokens (the one
// color source), so they cannot drift from it. Everything else here is the
// legacy palette, unchanged; new code should use the tokens directly.
const tk = colorTokens.light;
export const lightColors = {
  // Neutral gray scale (cool, matching the brand's #0A0A0A / #DCDCDC neutrals) —
  // no warm/tan cast, so surfaces sit cleanly against white cards.
  background: {
    primary: tk.bg, // Neutral light-gray ground
    secondary: tk.surface, // Cards, modals (float via shadow)
    tertiary: '#ECECED', // Elevated / inset surfaces
    quaternary: '#E3E3E5', // Form inputs, dropdowns, neutral buttons
    elevated: '#FFFFFF', // Highest elevation (shadow carries it)
    active: '#E7F3EB', // Active elements (light green tint)
    inactive: 'rgba(211,47,47,0.08)', // Inactive elements
    selected: '#EAF1FB', // Selected elements (light blue tint)
    highlight: '#EDEDEE', // Highlighted rows, selections
    default: '#171717', // Contrast background (dark, for use on light)
  },

  text: {
    primary: tk.text.primary, // Neutral near-black
    secondary: '#52525B',
    tertiary: '#7A7A82',
    quaternary: '#9C9CA4',
    disabled: '#BEBEC4',
    inverted: '#FFFFFF', // Text on dark surfaces
    // Text sitting ON a filled accent surface (brand / success / error button).
    // White in BOTH themes: the button's own color is the ground, not the
    // page's, so this must not flip with the theme the way `inverted` does.
    onAccent: tk.onAccent,
    // Small text that flags attention (past due, over target). The brand
    // scarlet itself is 3.8:1 on white, below AA for body-size text, so
    // attention copy uses this per-theme shade instead (AA in both themes).
    accent: tk.textAccent,
    placeholder: '#A1A1AA',
    selected: '#1769C4',
  },

  brand: {
    primary: tk.brand, // Brand scarlet — unchanged
    secondary: '#1958C4',
    accent: '#FF9500',
    dark: '#C84826',
    darker: '#B23E1F',
    // Solid scarlet SURFACE under text (primary buttons, filled badges).
    // Deeper than `primary` so white `text.onAccent` clears AA (4.8:1) at any
    // size; `primary` stays the brand mark / accent colour.
    fill: tk.brandFill,
    fillHover: tk.brandFillPressed,
    fillActive: '#9C3519',
  },

  status: {
    success: '#2E7D46', // Deepened for legibility on white
    successHover: '#276B3C',
    error: '#DC2626',
    errorHover: '#B91C1C',
    warning: '#B26A00', // Deepened orange
    warningHover: '#8F5600',
    info: '#1769C4', // Deepened blue
    infoHover: '#135AAB',
    neutral: '#6B7280',
  },

  interactive: {
    primary: '#E3522E', // Brand button — unchanged
    primaryHover: '#C84826',
    primaryActive: '#B23E1F',
    secondary: '#6B7280',
    secondaryHover: '#4B5563',
    secondaryActive: '#374151',
    disabled: '#C7C0BA', // Disabled control on light
    focus: '#0A6CE0',
  },

  border: {
    default: '#D6D6DA', // Neutral hairline — visible on the light ground
    light: '#E2E2E6', // Subtler divider
    lighter: '#C5C5CB', // Strongest neutral border
    focus: '#0A6CE0',
    error: '#DC2626',
    success: '#2E7D46',
    selected: '#1958C4',
    required: '#B26A00',
  },

  overlay: {
    backdrop: 'rgba(15,15,17,0.45)', // Modal backdrop still dims content
    light: 'rgba(0,0,0,0.04)', // Subtle inset on a light ground
    medium: 'rgba(0,0,0,0.07)',
    dark: 'rgba(0,0,0,0.40)',
    darker: 'rgba(0,0,0,0.55)',
    wash: 'rgba(0,0,0,0.03)', // white-wash flips to a faint black tint
    washStrong: 'rgba(0,0,0,0.05)',
    hairline: 'rgba(0,0,0,0.10)',
    scrim: 'rgba(0,0,0,0.16)',
  },

  workOrder: {
    completed: '#2E7D46',
    inProgress: '#B26A00',
    pending: '#1769C4',
    cancelled: '#DC2626',
    onHold: '#8221A0',
    draft: '#6B7280',
  },

  userStatus: {
    active: '#2E7D46',
    inactive: '#9A928B',
    suspended: '#DC2626',
    pending: '#B26A00',
    invited: '#1769C4',
  },

  locationStatus: {
    active: '#2E7D46',
    inactive: '#9A928B',
    maintenance: '#B26A00',
    closed: '#DC2626',
    opening: '#1769C4',
  },

  severity: {
    critical: '#DC2626',
    high: '#B26A00',
    medium: '#A8790C', // Yellow is invisible on white — deep goldenrod
    low: '#2E7D46',
    info: '#1769C4',
  },

  chart: {
    primary: '#0A6CE0',
    secondary: '#2AA3E0',
    tertiary: '#E07B00',
    quaternary: '#2E7D46',
    quinary: '#DC2626',
    senary: '#8221A0',
    gradient: {
      blue: 'linear-gradient(135deg, #0A6CE0 0%, #5AC8FA 100%)',
      green: 'linear-gradient(135deg, #2E7D46 0%, #8BC34A 100%)',
      orange: 'linear-gradient(135deg, #E07B00 0%, #FFB74D 100%)',
      red: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
    },
  },
};

export type LightColors = typeof lightColors;
